import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Fuel as FuelIcon, Plus, Trash2, Camera, Truck, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { uploadPhoto } from "@/lib/imageUpload";
import { AuthImage } from "@/components/AuthImage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { WeeklyFuelPrice, perLitre } from "./FuelPrices";

const PRICE_ROLES = ["admin", "office", "super_admin", "quantity_surveyor"];
const mondayOf = (iso) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toLocaleDateString("en-CA"); };
// Weekly price in force on a fill date (same rule as the server).
const priceFor = (prices, fuelType, iso) => (prices?.history || []).filter((p) => p.fuel_type === fuelType && p.week_of <= mondayOf(iso)).sort((a, b) => (a.week_of < b.week_of ? 1 : -1))[0] || null;

const fmt = (n) => `$${(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Fuel() {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [prices, setPrices] = useState(null);
  const canPrice = PRICE_ROLES.includes(user.role);

  const load = () => {
    api.get("/fuel").then((r) => setLogs(r.data)).finally(() => setLoaded(true));
    if (isOffice) api.get("/fuel/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/fuel/prices").then((r) => setPrices(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/fleet").then((r) => setFleet(r.data || [])).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCost = async (l) => {
    const val = window.prompt(`Cost for ${l.litres} L on ${l.vehicle_label} (${l.filled_at})?`, "");
    if (val == null || !val.trim()) return;
    try {
      await api.patch(`/fuel/${l.id}`, { cost_nzd: val });
      toast.success("Cost set");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set cost");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this fill-up?")) return;
    await api.delete(`/fuel/${id}`);
    toast.success("Fill-up removed");
    load();
  };

  return (
    <div data-testid="fuel-page" className="px-4 py-4 md:p-6 lg:p-10 md:max-w-7xl md:mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ Fleet running costs"
        title="Fuel"
        subtitle={isOffice ? "Every fill-up costed against your machines and jobs." : "Log diesel and petrol fill-ups as you go."}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="new-fuel-button">
                <Plus className="w-4 h-4 mr-2" /> Log fill-up
              </Button>
            </DialogTrigger>
            <FuelForm fleet={fleet} jobs={jobs} prices={prices} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />

      {canPrice && prices && <WeeklyFuelPrice prices={prices} onSaved={load} />}

      {isOffice && summary && (
        <>
          {summary.uncosted_count > 0 && (
            <div className="mb-4 px-4 py-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-sm text-amber-700 dark:text-amber-400 font-medium" data-testid="fuel-uncosted-banner">
              {summary.uncosted_count} bowser fill-up{summary.uncosted_count > 1 ? "s" : ""} still need{summary.uncosted_count > 1 ? "" : "s"} a cost — tap "Uncosted" below to set it.
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 dark:bg-white/10 border border-zinc-300 dark:border-white/10 mb-6">
            {[
              ["This month", fmt(summary.this_month.cost), `${summary.this_month.litres} L · ${summary.this_month.count} fills`],
              ["Last month", fmt(summary.last_month.cost), `${summary.last_month.litres} L · ${summary.last_month.count} fills`],
              ["All time", fmt(summary.all_time.cost), `${summary.all_time.litres} L · ${summary.all_time.count} fills`],
              ["Avg / fill", summary.all_time.count ? fmt(summary.all_time.cost / summary.all_time.count) : "—", "across all vehicles"],
            ].map(([label, value, sub]) => (
              <div key={label} className="bg-white dark:bg-[#101620] p-4" data-testid={`fuel-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
                <div className="mt-1 font-mono font-bold text-xl">{value}</div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-6">
            <RunningCostTable icon={Truck} title="By vehicle" rows={summary.by_vehicle} testid="fuel-by-vehicle" />
            <RunningCostTable icon={Briefcase} title="By job" rows={summary.by_job} testid="fuel-by-job" />
          </div>
        </>
      )}

      <div className="label-tag mb-3 flex items-center gap-1.5"><FuelIcon className="w-3 h-3" /> {isOffice ? "Recent fill-ups" : "My fill-ups"}</div>
      {!loaded ? null : logs.length === 0 ? (
        <EmptyState title="No fill-ups yet" description="Log the first diesel or petrol fill-up." />
      ) : (
        <div className="space-y-2" data-testid="fuel-log-list">
          {logs.map((l) => (
            <div key={l.id} className="hard-card p-4 flex items-center gap-4 flex-wrap" data-testid={`fuel-log-${l.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#FF5F15]/10 border border-[#FF5F15]/25 flex items-center justify-center shrink-0">
                <FuelIcon className="w-4.5 h-4.5 w-[18px] h-[18px] text-[#FF5F15]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm">{l.vehicle_label} <span className="font-normal text-zinc-500">· {l.fuel_type}{l.source === "bowser" ? ` · from ${l.source_asset_label || "yard bowser"}` : ""}</span></div>
                <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                  {l.filled_at} · {l.litres} L{l.odometer_or_hours ? ` · ${l.odometer_or_hours} hrs/km` : ""}{l.job_title ? ` · ${l.job_title}` : ""} · by {l.user_name}
                </div>
                {l.notes && <div className="text-xs text-zinc-500 mt-0.5 truncate">{l.notes}</div>}
              </div>
              {l.receipt_photo_path && (
                <button
                  type="button"
                  onClick={() => setLightbox(l.receipt_photo_path)}
                  className="w-10 h-10 rounded-md overflow-hidden border border-zinc-200 dark:border-white/10 shrink-0 cursor-zoom-in hover:ring-2 hover:ring-[#FF5F15] transition-shadow"
                  title="View receipt"
                  data-testid={`fuel-receipt-thumb-${l.id}`}
                >
                  <AuthImage path={l.receipt_photo_path} alt="Receipt" className="w-full h-full object-cover" />
                </button>
              )}
              {l.cost_nzd ? (
                <div className="shrink-0 text-right" data-testid={`fuel-cost-${l.id}`}>
                  <div className="font-mono font-bold text-lg">{fmt(l.cost_nzd)}</div>
                  {l.cost_auto && l.price_per_litre && <div className="text-[10px] text-zinc-500" data-testid={`fuel-auto-price-${l.id}`}>@ {perLitre(l.price_per_litre)} ex GST</div>}
                </div>
              ) : isOffice ? (
                <button onClick={() => setCost(l)}
                        className="shrink-0 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 hover:border-amber-500"
                        data-testid={`fuel-set-cost-${l.id}`}>
                  Uncosted — set $
                </button>
              ) : (
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400" data-testid={`fuel-uncosted-${l.id}`}>Uncosted</span>
              )}
              {isOffice && (
                <Button size="icon" variant="outline" className="h-8 w-8 rounded-lg text-zinc-500 hover:text-red-600 shrink-0"
                        onClick={() => remove(l.id)} data-testid={`fuel-delete-${l.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <PhotoLightbox path={lightbox} open={!!lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function RunningCostTable({ icon: Icon, title, rows, testid }) {
  return (
    <section className="hard-card p-5" data-testid={testid}>
      <div className="label-tag mb-3 flex items-center gap-1.5"><Icon className="w-3 h-3" /> Running cost {title.toLowerCase()}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-400 italic">Nothing logged yet.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.slice(0, 8).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 text-sm border-b border-zinc-100 dark:border-white/5 pb-1.5 last:border-0">
              <span className="truncate font-medium">{r.label}</span>
              <span className="font-mono text-[11px] text-zinc-500 shrink-0">{r.litres} L · {r.count}x</span>
              <span className="font-mono font-bold shrink-0">{fmt(r.cost)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FuelForm({ fleet, jobs, prices, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    vehicle_id: "", source_asset_id: "", job_id: "", fuel_type: "diesel", source: "station", litres: "", cost_nzd: "",
    odometer_or_hours: "", notes: "", filled_at: today, receipt_photo_path: null,
  });
  const bowserPrice = form.source === "bowser" ? priceFor(prices, form.fuel_type, form.filled_at || today) : null;
  const autoCost = bowserPrice && Number(form.litres) > 0 ? Math.round(Number(form.litres) * bowserPrice.price_per_litre * 100) / 100 : null;
  const bowsers = fleet.filter(v => /bowser/i.test(v.vehicle_type || "") && v.id !== form.vehicle_id);
  const receivingBowser = /bowser/i.test(fleet.find(v => v.id === form.vehicle_id)?.vehicle_type || "");
  const [busy, setBusy] = useState(false);
  const [upl, setUpl] = useState(0); // 0 idle, 1-99 uploading pct, 100 done
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onReceipt = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUpl(1);
    try {
      const res = await uploadPhoto(file, "fuel", (pct) => setUpl(Math.max(1, pct)));
      setForm((f) => ({ ...f, receipt_photo_path: res.path }));
      setUpl(100);
    } catch {
      toast.error("Receipt upload failed");
      setUpl(0);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error("Pick a vehicle, machine or bowser"); return; }
    if (!form.litres) { toast.error("Litres are required"); return; }
    if (form.source === "station" && !form.cost_nzd) { toast.error("Cost is required for station fills — pick Yard / mobile bowser if unknown"); return; }
    setBusy(true);
    try {
      await api.post("/fuel", { ...form, source_asset_id: form.source === "bowser" ? form.source_asset_id || null : null, odometer_or_hours: receivingBowser ? null : form.odometer_or_hours, job_id: form.job_id || null, cost_nzd: form.cost_nzd || null });
      toast.success("Fill-up logged");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save fill-up");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto" data-testid="fuel-form-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Log a fill-up</DialogTitle>
        <DialogDescription>Record fuel added to a vehicle, machine or bowser, or supplied from a bowser.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Asset receiving fuel *</Label>
          <Select value={form.vehicle_id} onValueChange={value => setForm(f => ({ ...f, vehicle_id: value, source_asset_id: f.source_asset_id === value ? "" : f.source_asset_id }))}>
            <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-vehicle-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {fleet.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.fleet_number || v.rego}{/bowser/i.test(v.vehicle_type || "") ? " · Fuel bowser" : ""}{v.make ? ` · ${v.make} ${v.model || ""}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Filled from</Label>
            <Select value={form.source} onValueChange={set("source")}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="station">Fuel station</SelectItem>
                <SelectItem value="bowser">Yard / mobile bowser</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <Label className="label-tag">Date</Label>
            <Input type="date" value={form.filled_at} onChange={set("filled_at")} className="mt-1.5 rounded-sm border-zinc-400 w-full min-w-0" data-testid="fuel-date-input" />
          </div>
        </div>
        {form.source === "bowser" && (
          <div>
            <Label className="label-tag">Source bowser</Label>
            <Select value={form.source_asset_id || "__yard__"} onValueChange={value => setForm(f => ({ ...f, source_asset_id: value === "__yard__" ? "" : value }))}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-bowser-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__yard__">Yard bowser / not listed</SelectItem>
                {bowsers.map(v => <SelectItem key={v.id} value={v.id}>{v.fleet_number || v.rego} · {v.make} {v.model || ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-1.5 text-xs text-zinc-500">Select the bowser supplying this fuel. Add mobile bowsers in Assets using the Fuel bowser trailer type.</p>
          </div>
        )}
        {receivingBowser && <p className="text-xs text-zinc-500">This records fuel added to the bowser. To record fuel dispensed, select the machine receiving it and choose this bowser as the source.</p>}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Fuel type</Label>
            <Select value={form.fuel_type} onValueChange={set("fuel_type")}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="diesel">Diesel</SelectItem>
                <SelectItem value="petrol">Petrol</SelectItem>
                <SelectItem value="adblue">AdBlue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Litres *</Label>
            <Input type="number" step="0.1" inputMode="decimal" value={form.litres} onChange={set("litres")} placeholder="e.g. 85" className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-litres-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Total cost $ {form.source === "bowser" ? "" : "*"}</Label>
            <Input type="number" step="0.01" inputMode="decimal" value={form.cost_nzd} onChange={set("cost_nzd")}
                   placeholder={form.source === "bowser" ? (bowserPrice ? (autoCost != null ? `auto ${fmt(autoCost)}` : "auto from weekly price") : "unknown — office will cost it") : "e.g. 240.50"}
                   className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-cost-input" />
            {form.source === "bowser" && bowserPrice && !form.cost_nzd && (
              <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400" data-testid="fuel-auto-cost-hint">
                {autoCost != null ? `${fmt(autoCost)} ex GST` : "Costed"} at {bowserPrice.week_of === mondayOf(today) ? "this week's" : "the week-of-fill"} price of {perLitre(bowserPrice.price_per_litre)} — no need to enter it.
              </p>
            )}
          </div>
          {!receivingBowser && <div>
            <Label className="label-tag">Hours / odometer</Label>
            <Input type="number" step="0.1" inputMode="decimal" value={form.odometer_or_hours} onChange={set("odometer_or_hours")} placeholder="optional" className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-odo-input" />
          </div>}
        </div>
        <div>
          <Label className="label-tag">Job (optional)</Label>
          <Select value={form.job_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, job_id: v === "__none__" ? "" : v }))}>
            <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-job-select"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tag">Receipt photo</Label>
          <label className="mt-1.5 flex items-center justify-center gap-2 border border-dashed border-zinc-400 p-3 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15] rounded-sm" data-testid="fuel-receipt-upload">
            <Camera className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">
              {upl === 100 || form.receipt_photo_path ? "Receipt attached ✓" : upl > 0 ? `Uploading ${upl}%…` : "Snap the receipt"}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={onReceipt} />
          </label>
        </div>
        <div>
          <Label className="label-tag">Notes</Label>
          <Input value={form.notes} onChange={set("notes")} placeholder="e.g. filled at Z Henderson" className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-notes-input" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy || (upl > 0 && upl < 100)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="fuel-submit-button">
            {busy ? "Saving…" : "Save fill-up"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
